import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Log } from '@/types/mission.types'
import { waypointTools, areaTools, corridorTools } from '@/constants/missionData'
import type { ZoneResponse } from '@/types/zones.types'

/**
 * UI and workflow state for mission planning.
 * Mission list and selection live in useMissionsState() (singleton, no persistence).
 */
export const useMissionStore = defineStore('mission-planning', () => {
  const isDragging = ref(false)
  const activeTool = ref<string | null>(null)
  const missionLogs = ref<Log[]>([])
  const isLogPannelOpen = ref(false)
  const isSyncing = ref<null | string>(null)
  const terrainProviderType = ref('cesium-world-terrain')
  const showMapLayer = ref(false)
  const allZones = ref<ZoneResponse[]>([])
  const isFetchingMissions = ref(false)
  const missionToLaunch = ref<{
    id: string
    index: number
    missionName?: string
    type?: 's3'
  } | null>(null)
  const isDrawing = ref(false)

  const isMissionTool = computed(() => {
    const allTools = [...waypointTools, ...areaTools, ...corridorTools]
    const isInMissionTools = allTools.some((tool) => tool.name === activeTool.value)
    return isInMissionTools && activeTool.value !== 'Select'
  })

  function resetMissionState() {
    missionToLaunch.value = null
  }

  /**
   * Clear mission-planning state that is tied to the current device group.
   *
   * - `activeTool` / `isDrawing`: stale tool selection (zone polygon, waypoint,
   *   etc.) must not carry over — category-cleanup paths assume the previous
   *   tool still targets the current device's Cesium state.
   * - `isSyncing`: per-mission sync flag; leaking it blocks future syncs.
   * - `isFetchingMissions`: gate-ref; leaking it can permanently block refetch.
   * - `missionLogs`: waypoint execution logs produced by the prior device.
   * - `missionToLaunch`: confirm-launch modal payload for a different device.
   *
   * UI prefs (`terrainProviderType`, `showMapLayer`, `isLogPannelOpen`) and
   * `allZones` (owned by `cleanupZoneEntities`) are intentionally untouched.
   */
  function $resetDeviceScopedState() {
    activeTool.value = null
    isSyncing.value = null
    isDrawing.value = false
    isFetchingMissions.value = false
    missionLogs.value = []
    missionToLaunch.value = null
  }

  return {
    isDragging,
    activeTool,
    missionLogs,
    isLogPannelOpen,
    isSyncing,
    terrainProviderType,
    showMapLayer,
    isMissionTool,
    isDrawing,
    allZones,
    missionToLaunch,
    resetMissionState,
    $resetDeviceScopedState,
    isFetchingMissions,
  }
})
